class BundleProduct extends HTMLElement {
  /** @type {Map<number, number>} */
  #appSelectSyncGenByBox = new Map();

  static selectors = {
    option: '.kt-product-bundle__option',
    removeButton: '.kt-product-bundle__preview-remove',
    previewBox: '.kt-product-bundle__preview-box',
    filledBox: '.kt-product-bundle__preview-box.is-filled',
    emptyBox: '.kt-product-bundle__preview-box:not(.is-filled)',
    selectedImage: '.kt-product-bundle__preview-selected-image',
    emptyLabel: '.kt-product-bundle__preview-empty',
    addToCartButton: '[data-js-product-add-to-cart]',
    addToCartText: '[data-js-product-add-to-cart-text]',
    form: '.kt-form-add-to-cart',
    productPage: '.kt-product-page',
  };

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.#updateAddToCartProgress();
    this.#preselectFromUrlParams();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  // -------------------------------------------------------------------------
  // Private event handler
  // -------------------------------------------------------------------------

  #handleClick = (event) => {
    const { option, removeButton } = BundleProduct.selectors;

    const removeBtn = event.target.closest(removeButton);
    if (removeBtn) {
      this.#handleRemove(removeBtn);
      return;
    }

    const optionEl = event.target.closest(option);
    if (optionEl) {
      this.#handleSelect(optionEl);
    }
  };

  // -------------------------------------------------------------------------
  // Private actions
  // -------------------------------------------------------------------------

  #handleSelect(option) {
    const { featuredImage, productTitle = '' } = option.dataset;
    if (!featuredImage) return;

    const emptyBox = this.querySelector(BundleProduct.selectors.emptyBox);
    if (!emptyBox) return;

    const appOptionValue = String(productTitle);
    this.#fillBox(emptyBox, featuredImage, productTitle, appOptionValue);
  }

  #handleRemove(removeButton) {
    const box = removeButton.closest(BundleProduct.selectors.previewBox);
    if (!box) return;

    this.#clearBox(box);
  }

  #preselectFromUrlParams() {
    const optionKeys = this.#getUrlPreselectedOptionKeys();
    if (!optionKeys.length) return;

    const maxBoxes = this.querySelectorAll(BundleProduct.selectors.previewBox).length;
    if (!maxBoxes) return;

    optionKeys.slice(0, maxBoxes).forEach((key) => {
      const option = this.#findOptionByKey(key);
      if (option) {
        this.#handleSelect(option);
      }
    });
  }

  #getUrlPreselectedOptionKeys() {
    const params = new URLSearchParams(window.location.search);
    const rawValues = [];

    for (let i = 1; i <= 3; i += 1) {
      const value = params.get(`box${i}`);
      if (value) rawValues.push(value);
    }

    return rawValues
      .map((value) => this.#normalizeKey(value))
      .filter(Boolean);
  }

  #findOptionByKey(key) {
    const options = this.querySelectorAll(BundleProduct.selectors.option);

    return Array.from(options).find((option) => {
      const {
        productId = '',
        productHandle = '',
        productTitle = '',
      } = option.dataset;

      return this.#normalizeKey(productId) === key
        || this.#normalizeKey(productHandle) === key
        || this.#normalizeKey(productTitle) === key;
    });
  }

  #normalizeKey(value) {
    return String(value).trim().toLowerCase();
  }

  #fillBox(box, imageSrc, title, appOptionValue) {
    const { selectedImage, emptyLabel } = BundleProduct.selectors;
    const image = box.querySelector(selectedImage);
    const label = box.querySelector(emptyLabel);

    if (image) { image.src = imageSrc; image.alt = title; }
    if (label) label.textContent = title;

    box.classList.add('is-filled');
    this.#startAppSelectSync(this.#previewBoxIndex(box), appOptionValue);
    this.#updateAddToCartProgress();
  }

  #clearBox(box) {
    const { selectedImage, emptyLabel } = BundleProduct.selectors;
    const image = box.querySelector(selectedImage);
    const label = box.querySelector(emptyLabel);
    const emptyLabelText = this.dataset.emptyLabel || '';

    if (image) { image.src = ''; image.alt = ''; }
    if (label) label.textContent = emptyLabelText;

    box.classList.remove('is-filled');
    this.#startAppSelectSync(this.#previewBoxIndex(box), null);
    this.#updateAddToCartProgress();
  }

  #previewBoxIndex(box) {
    const boxes = Array.from(this.querySelectorAll(BundleProduct.selectors.previewBox));
    return boxes.indexOf(box) + 1;
  }

  #startAppSelectSync(boxIndex, optionValue) {
    const next = (this.#appSelectSyncGenByBox.get(boxIndex) ?? 0) + 1;
    this.#appSelectSyncGenByBox.set(boxIndex, next);
    this.#runAppSelectSync(boxIndex, optionValue, 0, next);
  }

  // App-injected `<select id="box-{n}">` (option values = exact product title). Retries until present.
  #runAppSelectSync(boxIndex, optionValue, attempt, gen) {
    if (gen !== this.#appSelectSyncGenByBox.get(boxIndex)) return;

    const maxAttempts = 24;
    const delayMs = Math.min(40 + attempt * 35, 450);
    const select = this.querySelector(`#box-${boxIndex}`);

    if (!select || select.tagName !== 'SELECT' || !select.options?.length) {
      if (attempt < maxAttempts) {
        window.setTimeout(() => this.#runAppSelectSync(boxIndex, optionValue, attempt + 1, gen), delayMs);
      }
      return;
    }

    if (optionValue) {
      const optIndex = Array.from(select.options).findIndex((opt) => opt.value === optionValue);
      if (optIndex < 0) {
        if (attempt < maxAttempts) {
          window.setTimeout(() => this.#runAppSelectSync(boxIndex, optionValue, attempt + 1, gen), delayMs);
        }
        return;
      }
      select.selectedIndex = optIndex;
    } else {
      const emptyIdx = Array.from(select.options).findIndex((opt) => opt.value === '');
      select.selectedIndex = emptyIdx >= 0 ? emptyIdx : 0;
    }

    if (gen !== this.#appSelectSyncGenByBox.get(boxIndex)) return;

    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #updateAddToCartProgress() {
    const { previewBox, filledBox, addToCartButton, productPage } = BundleProduct.selectors;
    const totalBoxes = this.querySelectorAll(previewBox).length;
    const filledBoxes = this.querySelectorAll(filledBox).length;
    const isComplete = totalBoxes > 0 && filledBoxes === totalBoxes;

    const fillPercentage = totalBoxes > 0
      ? `${Math.round((filledBoxes / totalBoxes) * 100)}%`
      : '0%';

    const addToCartButtonEl = this.closest(productPage)?.querySelector(addToCartButton)
      ?? document.querySelector(addToCartButton);

    if (!addToCartButtonEl) return;

    addToCartButtonEl.classList.add('kt-button--bundle-progress');
    addToCartButtonEl.classList.add('kt-button--bundle-layout');
    addToCartButtonEl.style.setProperty('--kt-bundle-atc-fill', fillPercentage);
    addToCartButtonEl.disabled = !isComplete;
    addToCartButtonEl.classList.toggle('kt-disabled', !isComplete);
    addToCartButtonEl.setAttribute('aria-disabled', String(!isComplete));
    this.#updateAddToCartText(addToCartButtonEl, totalBoxes, filledBoxes);
  }

  #updateAddToCartText(button, totalBoxes, filledBoxes) {
    const { addToCartText } = BundleProduct.selectors;
    const textNodes = button.querySelectorAll(addToCartText);
    const addBoxTemplate = this.dataset.addBoxTemplate || '';
    const addBoxesTemplate = this.dataset.addBoxesTemplate || '';

    if (!textNodes.length) return;

    const remaining = Math.max(totalBoxes - filledBoxes, 0);

    textNodes.forEach((node) => {
      if (!node.dataset.bundleOriginalText) {
        node.dataset.bundleOriginalText = node.textContent;
      }

      const addMoreText = remaining === 1
        ? addBoxTemplate.replace('[count]', remaining)
        : addBoxesTemplate.replace('[count]', remaining);

      node.textContent = remaining > 0
        ? addMoreText
        : node.dataset.bundleOriginalText;
    });
  }
}

if (!customElements.get('bundle-product')) {
  customElements.define('bundle-product', BundleProduct);
}
